"use client";

import { c as _c } from "react/compiler-runtime";
import ProfileAvatar, { ProfileBadgeSize } from "@/components/common/profile/ProfileAvatar";
import MediaTypeBadge from "@/components/drops/media/MediaTypeBadge";
import InteractiveIcon from "@/components/drops/media/InteractiveIcon";
import MediaDisplay from "@/components/drops/view/item/content/media/MediaDisplay";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { ImageScale } from "@/helpers/image.helpers";
import { type ExtendedDrop } from "@/helpers/waves/drop.helpers";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import Link from "next/link";
import { useEffect, useState } from "react";
interface LeadingCardProps {
  readonly drop: ExtendedDrop;
  readonly rank: number;
}
const getRankLabelClass = (rank: number) => {
  if (rank === 1) return "tw-text-[#E8D48A]";
  if (rank === 2) return "tw-text-[#DDDDDD]";
  if (rank === 3) return "tw-text-[#CD7F32]";
  return "tw-text-white/60";
};
export const LeadingCard = t0 => {
  const $ = _c(56);
  const {
    drop,
    rank
  } = t0;
  const {
    hasTouchScreen
  } = useDeviceInfo();
  const media = drop.parts[0]?.media[0];
  const isTabletOrSmaller = useMediaQuery("(max-width: 1023px)");
  const mediaImageScale = isTabletOrSmaller ? ImageScale.AUTOx450 : ImageScale.AUTOx600;
  const isHtml = media?.mime_type === "text/html";
  const shouldGate = hasTouchScreen && isHtml;
  const [interactiveEnabled, setInteractiveEnabled] = useState(!shouldGate);
  let t1;
  if ($[0] !== shouldGate) {
    t1 = () => {
      if (shouldGate) {
        setInteractiveEnabled(false);
        return;
      }
      setInteractiveEnabled(true);
    };
    $[0] = shouldGate;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== drop.id || $[3] !== shouldGate) {
    t2 = [drop.id, shouldGate];
    $[2] = drop.id;
    $[3] = shouldGate;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useEffect(t1, t2);
  const title = drop.title ?? drop.metadata.find(_temp)?.data_value ?? "Untitled";
  const author = drop.author;
  let t3;
  if ($[5] !== rank) {
    t3 = getRankLabelClass(rank);
    $[5] = rank;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const rankLabelClass = t3;
  let t4;
  bb0: {
    if (!media) {
      let t5;
      if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
        t5 = <div className="tw-flex tw-size-full tw-items-center tw-justify-center tw-bg-iron-950"><span className="tw-text-sm tw-text-white/40">No image</span></div>;
        $[7] = t5;
      } else {
        t5 = $[7];
      }
      t4 = t5;
      break bb0;
    }
    if (shouldGate && !interactiveEnabled) {
      let t5;
      if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
        t5 = () => setInteractiveEnabled(true);
        $[8] = t5;
      } else {
        t5 = $[8];
      }
      let t6;
      if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = <div className="tw-relative tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-bg-iron-950/30"><button type="button" onClick={t5} className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-transparent" aria-label="Load interactive media"><span className="tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border tw-border-white/20 tw-bg-iron-950/90 tw-px-4 tw-py-2 tw-text-xs tw-font-semibold tw-text-white tw-shadow-[0_16px_40px_rgba(0,0,0,0.65)] tw-ring-1 tw-ring-white/10 tw-transition hover:tw-bg-iron-900"><InteractiveIcon className="tw-size-4 tw-flex-shrink-0" />Tap to load</span></button></div>;
        $[9] = t6;
      } else {
        t6 = $[9];
      }
      t4 = t6;
      break bb0;
    }
    let t5;
    if ($[10] !== media.mime_type || $[11] !== media.url || $[12] !== mediaImageScale) {
      t5 = <MediaDisplay media_mime_type={media.mime_type} media_url={media.url} disableMediaInteraction={true} imageScale={mediaImageScale} />;
      $[10] = media.mime_type;
      $[11] = media.url;
      $[12] = mediaImageScale;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    t4 = t5;
  }
  const mediaContent = t4;
  const t5 = `tw-text-[11px] tw-font-semibold tw-uppercase tw-leading-5 tw-tracking-wide ${rankLabelClass}`;
  const t6 = rank === 1 ? "LEADING" : `CURRENT TOP ${rank}`;
  let t7;
  if ($[14] !== t5 || $[15] !== t6) {
    t7 = <span className={t5}>{t6}</span>;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== drop.rating) {
    t8 = formatNumberWithCommas(drop.rating);
    $[17] = drop.rating;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t8) {
    t9 = <span className="tw-text-white/80">{t8}</span>;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <span className="tw-text-white/50"> TDH</span>;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t9) {
    t11 = <span className="tw-font-mono tw-text-xs">{t9}{t10}</span>;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11 || $[25] !== t7) {
    t12 = <div className="tw-flex tw-flex-col tw-items-start tw-gap-1 tw-border-b tw-border-white/10 tw-bg-iron-900 tw-px-2.5 tw-py-1.5 @sm:tw-flex-row @sm:tw-items-center @sm:tw-justify-between sm:tw-px-3 sm:tw-py-2">{t7}{t11}</div>;
    $[24] = t11;
    $[25] = t7;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== mediaContent) {
    t13 = <div className="tw-relative tw-flex tw-aspect-[3/4] tw-max-h-[clamp(320px,70vw,500px)] tw-w-full tw-items-center tw-justify-center tw-overflow-hidden tw-bg-iron-950 tw-p-2 sm:tw-p-3"><div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-transition-transform tw-duration-700 tw-ease-out group-hover:tw-scale-105">{mediaContent}</div></div>;
    $[27] = mediaContent;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t12 || $[30] !== t13) {
    t14 = <div className="tw-flex tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-white/10 tw-bg-iron-950 tw-transition-colors group-hover:tw-border-white/10">{t12}{t13}</div>;
    $[29] = t12;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const t15 = media?.mime_type;
  let t16;
  if ($[32] !== drop.id || $[33] !== t15) {
    t16 = <MediaTypeBadge mimeType={t15} dropId={drop.id} size="sm" />;
    $[32] = drop.id;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  const t17 = `/waves?wave=${drop.wave.id}&drop=${drop.id}`;
  let t18;
  if ($[35] !== t17 || $[36] !== title) {
    t18 = <Link href={t17} className="tw-m-0 tw-line-clamp-2 tw-text-base tw-font-semibold tw-leading-tight tw-text-iron-200 tw-no-underline tw-transition-colors group-hover:tw-text-white @lg:tw-line-clamp-1">{title}</Link>;
    $[35] = t17;
    $[36] = title;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== t16 || $[39] !== t18) {
    t19 = <div className="tw-flex tw-items-center tw-gap-2">{t16}{t18}</div>;
    $[38] = t16;
    $[39] = t18;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  const t20 = `/${author.handle ?? author.primary_address}`;
  const t21 = author.handle ?? "Artist";
  let t22;
  if ($[41] !== author.pfp || $[42] !== t21) {
    t22 = <ProfileAvatar pfpUrl={author.pfp} alt={t21} size={ProfileBadgeSize.SMALL} />;
    $[41] = author.pfp;
    $[42] = t21;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  const t23 = author.handle ?? "Anonymous";
  let t24;
  if ($[44] !== t23) {
    t24 = <span className="tw-min-w-0 tw-truncate tw-text-xs tw-text-white/50 desktop-hover:hover:tw-text-white">{t23}</span>;
    $[44] = t23;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  let t25;
  if ($[46] !== t20 || $[47] !== t22 || $[48] !== t24) {
    t25 = <Link href={t20} className="tw-mt-2 tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-no-underline">{t22}{t24}</Link>;
    $[46] = t20;
    $[47] = t22;
    $[48] = t24;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  let t26;
  if ($[50] !== t19 || $[51] !== t25) {
    t26 = <div className="tw-flex tw-flex-col tw-gap-3"><div>{t19}{t25}</div></div>;
    $[50] = t19;
    $[51] = t25;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  let t27;
  if ($[53] !== t14 || $[54] !== t26) {
    t27 = <div className="tw-group tw-flex tw-flex-col tw-gap-3 tw-text-left tw-transition-all tw-duration-500 desktop-hover:tw-opacity-70 desktop-hover:tw-grayscale desktop-hover:hover:tw-opacity-100 desktop-hover:hover:tw-grayscale-0 sm:tw-gap-4">{t14}{t26}</div>;
    $[53] = t14;
    $[54] = t26;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  return t27;
};
function _temp(m) {
  return m.data_key === "title";
}